import React, { useState, useEffect } from 'react';
import './AdminPanel.css';

interface Config {
  // Basic pricing
  demandFactor: number;
  
  // Price bounds
  minPriceMultiplier: number;
  maxPriceMultiplier: number;
  
  // Market fluctuation
  baseFluctuation: number;
  maxSingleChange: number;
  
  // Time effects
  timeEffectsEnabled: boolean;
  happyHourStart: number;
  happyHourEnd: number;
  happyHourDiscount: number;
  rushHourStart: number;
  rushHourEnd: number;
  rushHourVolatility: number;
  
  // Market sentiment
  sentimentEnabled: boolean;
  sentimentMinDuration: number;
  sentimentMaxDuration: number;
  bullishMultiplierMin: number;
  bullishMultiplierMax: number;
  bearishMultiplierMin: number;
  bearishMultiplierMax: number;
  
  // Competitor effects
  competitorEnabled: boolean;
  sameCategoryEffect: number;
  otherCategoryEffect: number;
  
  // Time drift
  timeDriftEnabled: boolean;
  timeDriftFactor: number;
  
  // Popularity volatility
  popularityVolatilityEnabled: boolean;
  popularityThreshold: number;
  maxVolatilityMultiplier: number;
  volatilityIncrement: number;
  
  // Update intervals
  priceUpdateInterval: number;
  
  // Reset prices on startup
  resetPricesOnStartup: boolean;
}

const AdminPanel: React.FC = () => {
  const [config, setConfig] = useState<Config | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [activeSection, setActiveSection] = useState('basic');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await fetch('https://boersensaufen.fwne.de:1234/api/config');
      const data = await response.json();
      setConfig(data);
    } catch (error) {
      setMessage('❌ Fehler beim Laden der Konfiguration');
    }
  };

  const saveConfig = async () => {
    if (!config) return;
    
    setLoading(true);
    setMessage('');
    
    try {
      const response = await fetch('https://boersensaufen.fwne.de:1234/api/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      
      if (response.ok) {
        setMessage('✅ Konfiguration erfolgreich gespeichert!');
      } else {
        setMessage('❌ Fehler beim Speichern');
      }
    } catch (error) {
      setMessage('❌ Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  };

  const resetSystem = async () => {
    if (!window.confirm('🔄 System wirklich zurücksetzen? Alle Preise und Verkäufe werden gelöscht!')) {
      return;
    }
    
    setLoading(true);
    setMessage('');
    
    try {
      const response = await fetch('https://boersensaufen.fwne.de:1234/api/reset', {
        method: 'POST'
      });
      
      if (response.ok) {
        setMessage('✅ System erfolgreich zurückgesetzt!');
      } else {
        setMessage('❌ Fehler beim Zurücksetzen');
      }
    } catch (error) {
      setMessage('❌ Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  };

  const updateConfig = (key: keyof Config, value: any) => {
    if (!config) return;
    setConfig({ ...config, [key]: value });
  };

  if (!config) {
    return <div className="admin-panel loading">Lade Konfiguration...</div>;
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h2>🔧 Admin-Panel</h2>
        <div className="admin-actions">
          <button 
            className="save-button" 
            onClick={saveConfig}
            disabled={loading}
          >
            {loading ? 'Speichere...' : '💾 Speichern'}
          </button>
          <button 
            className="reset-button" 
            onClick={resetSystem}
            disabled={loading}
          >
            🔄 System Reset
          </button>
        </div>
      </div>

      {message && (
        <div className={`admin-message ${message.includes('✅') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      <div className="admin-content">
        <div className="admin-sidebar">
          <button 
            className={`section-button ${activeSection === 'basic' ? 'active' : ''}`}
            onClick={() => setActiveSection('basic')}
          >
            💰 Grundeinstellungen
          </button>
          <button 
            className={`section-button ${activeSection === 'time' ? 'active' : ''}`}
            onClick={() => setActiveSection('time')}
          >
            🕐 Zeit-Effekte
          </button>
          <button 
            className={`section-button ${activeSection === 'market' ? 'active' : ''}`}
            onClick={() => setActiveSection('market')}
          >
            📈 Markt-Sentiment
          </button>
          <button 
            className={`section-button ${activeSection === 'advanced' ? 'active' : ''}`}
            onClick={() => setActiveSection('advanced')}
          >
            ⚙️ Erweitert
          </button>
        </div>

        <div className="admin-main">
          {activeSection === 'basic' && (
            <div className="config-section">
              <h3>💰 Grundeinstellungen</h3>
              
              <div className="config-group">
                <label>Nachfrage-Faktor (Preisänderung pro Kauf)</label>
                <input
                  type="number"
                  step="0.001"
                  value={config.demandFactor}
                  onChange={(e) => updateConfig('demandFactor', parseFloat(e.target.value))}
                />
                <span className="config-help">{(config.demandFactor * 100).toFixed(1)}% pro Kauf</span>
              </div>

              <div className="config-group">
                <label>Minimaler Preis (% vom Basispreis)</label>
                <input
                  type="number"
                  step="0.1"
                  value={config.minPriceMultiplier}
                  onChange={(e) => updateConfig('minPriceMultiplier', parseFloat(e.target.value))}
                />
                <span className="config-help">{(config.minPriceMultiplier * 100).toFixed(0)}% minimum</span>
              </div>

              <div className="config-group">
                <label>Maximaler Preis (% vom Basispreis)</label>
                <input
                  type="number"
                  step="0.1"
                  value={config.maxPriceMultiplier}
                  onChange={(e) => updateConfig('maxPriceMultiplier', parseFloat(e.target.value))}
                />
                <span className="config-help">{(config.maxPriceMultiplier * 100).toFixed(0)}% maximum</span>
              </div>

              <div className="config-group">
                <label>Basis-Volatilität</label>
                <input
                  type="number"
                  step="0.001"
                  value={config.baseFluctuation}
                  onChange={(e) => updateConfig('baseFluctuation', parseFloat(e.target.value))}
                />
                <span className="config-help">±{(config.baseFluctuation * 100).toFixed(1)}% zufällige Schwankung</span>
              </div>

              <div className="config-group">
                <label>Max. Einzelkauf-Änderung</label>
                <input
                  type="number"
                  step="0.1"
                  value={config.maxSingleChange}
                  onChange={(e) => updateConfig('maxSingleChange', parseFloat(e.target.value))}
                />
                <span className="config-help">Max {(config.maxSingleChange * 100).toFixed(0)}% pro Kauf</span>
              </div>
            </div>
          )}

          {activeSection === 'time' && (
            <div className="config-section">
              <h3>🕐 Zeit-Effekte</h3>
              
              <div className="config-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={config.timeEffectsEnabled}
                    onChange={(e) => updateConfig('timeEffectsEnabled', e.target.checked)}
                  />
                  Zeit-Effekte aktivieren
                </label>
                <span className="config-help">Happy Hour und Rush Hour ein/ausschalten</span>
              </div>

              {config.timeEffectsEnabled && (
                <>
                  <div className="config-group">
                    <label>Happy Hour Start</label>
                <input
                  type="number"
                  min="0"
                  max="23"
                  value={config.happyHourStart}
                  onChange={(e) => updateConfig('happyHourStart', parseInt(e.target.value))}
                />
                <span className="config-help">{config.happyHourStart}:00 Uhr</span>
              </div>

              <div className="config-group">
                <label>Happy Hour Ende</label>
                <input
                  type="number"
                  min="0"
                  max="23"
                  value={config.happyHourEnd}
                  onChange={(e) => updateConfig('happyHourEnd', parseInt(e.target.value))}
                />
                <span className="config-help">{config.happyHourEnd}:00 Uhr</span>
              </div>

              <div className="config-group">
                <label>Happy Hour Rabatt</label>
                <input
                  type="number"
                  step="0.01"
                  value={config.happyHourDiscount}
                  onChange={(e) => updateConfig('happyHourDiscount', parseFloat(e.target.value))}
                />
                <span className="config-help">{(config.happyHourDiscount * 100).toFixed(0)}% Rabatt</span>
              </div>

              <div className="config-group">
                <label>Rush Hour Start</label>
                <input
                  type="number"
                  min="0"
                  max="23"
                  value={config.rushHourStart}
                  onChange={(e) => updateConfig('rushHourStart', parseInt(e.target.value))}
                />
                <span className="config-help">{config.rushHourStart}:00 Uhr</span>
              </div>

              <div className="config-group">
                <label>Rush Hour Ende</label>
                <input
                  type="number"
                  min="0"
                  max="23"
                  value={config.rushHourEnd}
                  onChange={(e) => updateConfig('rushHourEnd', parseInt(e.target.value))}
                />
                <span className="config-help">{config.rushHourEnd}:00 Uhr</span>
              </div>

              <div className="config-group">
                <label>Rush Hour Volatilität</label>
                <input
                  type="number"
                  step="0.01"
                  value={config.rushHourVolatility}
                  onChange={(e) => updateConfig('rushHourVolatility', parseFloat(e.target.value))}
                />
                <span className="config-help">+{(config.rushHourVolatility * 100).toFixed(0)}% Volatilität</span>
              </div>

              <div className="config-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={config.timeDriftEnabled}
                    onChange={(e) => updateConfig('timeDriftEnabled', e.target.checked)}
                  />
                  Zeit-Drift aktivieren
                </label>
                <span className="config-help">Preise driften langsam zum Basispreis zurück</span>
              </div>

              {config.timeDriftEnabled && (
                <div className="config-group">
                  <label>Zeit-Drift Faktor</label>
                  <input
                    type="number"
                    step="0.001"
                    value={config.timeDriftFactor}
                    onChange={(e) => updateConfig('timeDriftFactor', parseFloat(e.target.value))}
                  />
                  <span className="config-help">{(config.timeDriftFactor * 100).toFixed(1)}% Rückkehr pro Interval</span>
                </div>
              )}
                </>
              )}
            </div>
          )}

          {activeSection === 'market' && (
            <div className="config-section">
              <h3>📈 Markt-Sentiment</h3>
              
              <div className="config-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={config.sentimentEnabled}
                    onChange={(e) => updateConfig('sentimentEnabled', e.target.checked)}
                  />
                  Markt-Sentiment aktivieren
                </label>
                <span className="config-help">Bull/Bear Markets simulieren</span>
              </div>

              {config.sentimentEnabled && (
                <>
                  <div className="config-group">
                    <label>Min. Sentiment-Dauer (Minuten)</label>
                    <input
                      type="number"
                      min="1"
                      value={config.sentimentMinDuration}
                      onChange={(e) => updateConfig('sentimentMinDuration', parseInt(e.target.value))}
                    />
                    <span className="config-help">{config.sentimentMinDuration} Minuten minimum</span>
                  </div>

                  <div className="config-group">
                    <label>Max. Sentiment-Dauer (Minuten)</label>
                    <input
                      type="number"
                      min="1"
                      value={config.sentimentMaxDuration}
                      onChange={(e) => updateConfig('sentimentMaxDuration', parseInt(e.target.value))}
                    />
                    <span className="config-help">{config.sentimentMaxDuration} Minuten maximum</span>
                  </div>

                  <div className="config-group">
                    <label>Bull Market Min. Multiplikator</label>
                    <input
                      type="number"
                      step="0.01"
                      value={config.bullishMultiplierMin}
                      onChange={(e) => updateConfig('bullishMultiplierMin', parseFloat(e.target.value))}
                    />
                    <span className="config-help">+{((config.bullishMultiplierMin - 1) * 100).toFixed(0)}% minimum</span>
                  </div>

                  <div className="config-group">
                    <label>Bull Market Max. Multiplikator</label>
                    <input
                      type="number"
                      step="0.01"
                      value={config.bullishMultiplierMax}
                      onChange={(e) => updateConfig('bullishMultiplierMax', parseFloat(e.target.value))}
                    />
                    <span className="config-help">+{((config.bullishMultiplierMax - 1) * 100).toFixed(0)}% maximum</span>
                  </div>

                  <div className="config-group">
                    <label>Bear Market Min. Multiplikator</label>
                    <input
                      type="number"
                      step="0.01"
                      value={config.bearishMultiplierMin}
                      onChange={(e) => updateConfig('bearishMultiplierMin', parseFloat(e.target.value))}
                    />
                    <span className="config-help">{((config.bearishMultiplierMin - 1) * 100).toFixed(0)}% minimum</span>
                  </div>

                  <div className="config-group">
                    <label>Bear Market Max. Multiplikator</label>
                    <input
                      type="number"
                      step="0.01"
                      value={config.bearishMultiplierMax}
                      onChange={(e) => updateConfig('bearishMultiplierMax', parseFloat(e.target.value))}
                    />
                    <span className="config-help">{((config.bearishMultiplierMax - 1) * 100).toFixed(0)}% maximum</span>
                  </div>
                </>
              )}
            </div>
          )}

          {activeSection === 'advanced' && (
            <div className="config-section">
              <h3>⚙️ Erweiterte Einstellungen</h3>
              
              <div className="config-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={config.competitorEnabled}
                    onChange={(e) => updateConfig('competitorEnabled', e.target.checked)}
                  />
                  Konkurrenz-Effekte aktivieren
                </label>
                <span className="config-help">Andere Getränke bei Kauf beeinflussen</span>
              </div>

              {config.competitorEnabled && (
                <>
                  <div className="config-group">
                    <label>Gleiche Kategorie Effekt</label>
                    <input
                      type="number"
                      step="0.001"
                      value={config.sameCategoryEffect}
                      onChange={(e) => updateConfig('sameCategoryEffect', parseFloat(e.target.value))}
                    />
                    <span className="config-help">{(config.sameCategoryEffect * 100).toFixed(1)}% pro Kauf</span>
                  </div>

                  <div className="config-group">
                    <label>Andere Kategorie Effekt</label>
                    <input
                      type="number"
                      step="0.001"
                      value={config.otherCategoryEffect}
                      onChange={(e) => updateConfig('otherCategoryEffect', parseFloat(e.target.value))}
                    />
                    <span className="config-help">{(config.otherCategoryEffect * 100).toFixed(1)}% pro Kauf</span>
                  </div>
                </>
              )}

              <div className="config-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={config.popularityVolatilityEnabled}
                    onChange={(e) => updateConfig('popularityVolatilityEnabled', e.target.checked)}
                  />
                  Popularitäts-Volatilität aktivieren
                </label>
                <span className="config-help">Beliebte Getränke haben höhere Schwankungen</span>
              </div>

              {config.popularityVolatilityEnabled && (
                <>
                  <div className="config-group">
                    <label>Popularitäts-Schwelle</label>
                    <input
                      type="number"
                      min="1"
                      value={config.popularityThreshold}
                      onChange={(e) => updateConfig('popularityThreshold', parseInt(e.target.value))}
                    />
                    <span className="config-help">{config.popularityThreshold} Käufe für max. Volatilität</span>
                  </div>

                  <div className="config-group">
                    <label>Max. Volatilitäts-Multiplikator</label>
                    <input
                      type="number"
                      step="0.1"
                      value={config.maxVolatilityMultiplier}
                      onChange={(e) => updateConfig('maxVolatilityMultiplier', parseFloat(e.target.value))}
                    />
                    <span className="config-help">Bis zu {config.maxVolatilityMultiplier}x Volatilität</span>
                  </div>
                </>
              )}

              <div className="config-group">
                <label>Preis-Update Intervall (ms)</label>
                <input
                  type="number"
                  step="1000"
                  value={config.priceUpdateInterval}
                  onChange={(e) => updateConfig('priceUpdateInterval', parseInt(e.target.value))}
                />
                <span className="config-help">Alle {config.priceUpdateInterval / 1000} Sekunden</span>
              </div>

              <div className="config-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={config.resetPricesOnStartup}
                    onChange={(e) => updateConfig('resetPricesOnStartup', e.target.checked)}
                  />
                  Preise beim Start zurücksetzen
                </label>
                <span className="config-help">Server startet immer mit Basispreisen</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;